import { isNotFound } from '@tanstack/react-router';
import { Suspense } from 'react';
import { Skeleton } from '@worldcoo/ui/components/skeleton';
import { useLingui } from '@lingui/react/macro';
import { Card, CardHeader, CardTitle, CardContent } from '@worldcoo/ui/components/card';
import { DivWithPrimaryColorVariable } from './div-with-primary-color-variable';
import { ObjectivesIcon } from './assets/campaign-texts/objectives-icon';
import { DescriptionIcon } from './assets/campaign-texts/description-icon';
import { BeneficiariesIcon } from './assets/campaign-texts/beneficiaries-icon';
import { UpdatesAndLinksIcon } from './assets/campaign-texts/updates-and-links-icon';
import { usePortalCampaignQuery } from '@/lib/data-hooks';
import { cn, heroImageSize } from '@worldcoo/ui/lib/utils';
import { ActivityIcon } from './assets/campaign-texts/activity-icon';
import { useCampaignDisplayContext } from './campaign-display-context';

export function Campaign(props: { campaignId: string }) {
	const displayData = useCampaignDisplayContext();
	if (displayData) {
		return <CampaignContent name={displayData.campaign.texts.name} texts={displayData.campaign.texts} />;
	}
	return (
		<Suspense
			fallback={
				<div className="flex flex-col lg:flex-row">
					<section className="flex w-full flex-col gap-6 px-6 pb-10 lg:w-1/2">
						<Skeleton className="bg-secondary h-9 w-32" />

						<div className="flex flex-col gap-1">
							<Skeleton className="bg-secondary h-6 w-lg" />
							<Skeleton className="bg-secondary h-6 w-md" />
							<Skeleton className="bg-secondary h-6 w-lg" />
							<br />
							<Skeleton className="bg-secondary h-6 w-lg" />
							<Skeleton className="bg-secondary h-6 w-md" />
							<br />
							<Skeleton className="bg-secondary h-6 w-lg" />
							<Skeleton className="bg-secondary h-6 w-md" />
							<Skeleton className="bg-secondary h-6 w-sm" />
						</div>

						<Skeleton className="bg-secondary h-14 w-48" />
					</section>

					<div className="w-full p-10 pb-20 lg:w-1/2">
						<Skeleton className={cn('bg-secondary h-full w-full rounded-2xl', heroImageSize.className)} />
					</div>
				</div>
			}
		>
			<SuspensedCampaign campaignId={props.campaignId} />
		</Suspense>
	);
}

export function CampaignTextCards({
	texts,
}: {
	texts: {
		objectives?: string;
		resume?: string;
		activities?: string;
		benefits?: string;
		updateAndLinks?: string;
	};
}) {
	const { t } = useLingui();
	return (
		<>
			{isNotEmpty(texts.objectives) && (
				<Card className="shadow-4xl gap-0 border-none px-6 py-12">
					<CardHeader>
						<CardTitle className="flex items-center gap-2 text-2xl">
							<ObjectivesIcon className="text-primary" />
							{t`Campaign objectives`}
						</CardTitle>
					</CardHeader>
					<CardContent>
						<div
							className="unreset-tailwind"
							dangerouslySetInnerHTML={{
								__html: texts.objectives,
							}}
						/>
					</CardContent>
				</Card>
			)}

			{isNotEmpty(texts.resume) && (
				<Card className="shadow-4xl gap-0 border-none px-6 py-12">
					<CardHeader>
						<CardTitle className="flex items-center gap-2 text-2xl">
							<DescriptionIcon className="text-primary" />
							{t`Description`}
						</CardTitle>
					</CardHeader>
					<CardContent>
						<div
							className="unreset-tailwind"
							dangerouslySetInnerHTML={{
								__html: texts.resume,
							}}
						/>
					</CardContent>
				</Card>
			)}

			{isNotEmpty(texts.activities) && (
				<Card className="shadow-4xl gap-0 border-none px-6 py-12">
					<CardHeader>
						<CardTitle className="flex items-center gap-2 text-2xl">
							<ActivityIcon className="text-primary" />
							{t`Activities`}
						</CardTitle>
					</CardHeader>
					<CardContent>
						{/* TODO: Make an accordion */}
						<div
							className="unreset-tailwind"
							dangerouslySetInnerHTML={{
								__html: texts.activities,
							}}
						/>
					</CardContent>
				</Card>
			)}

			{isNotEmpty(texts.benefits) && (
				<Card className="shadow-4xl gap-0 border-none px-6 py-12">
					<CardHeader>
						<CardTitle className="flex items-center gap-2 text-2xl">
							<BeneficiariesIcon className="text-primary" />
							{t`Who will benefit?`}
						</CardTitle>
					</CardHeader>
					<CardContent>
						<div
							className="unreset-tailwind"
							dangerouslySetInnerHTML={{
								__html: texts.benefits,
							}}
						/>
					</CardContent>
				</Card>
			)}

			{isNotEmpty(texts.updateAndLinks) && (
				<Card className="shadow-4xl gap-0 border-none px-6 py-12">
					<CardHeader>
						<CardTitle className="flex items-center gap-2 text-2xl">
							<UpdatesAndLinksIcon className="text-primary" />
							{t`More information and links`}
						</CardTitle>
					</CardHeader>
					<CardContent>
						<div
							className="unreset-tailwind"
							dangerouslySetInnerHTML={{
								__html: texts.updateAndLinks,
							}}
						/>
					</CardContent>
				</Card>
			)}
		</>
	);
}

function SuspensedCampaign(props: { campaignId: string }) {
	const campaign = usePortalCampaignQuery(props.campaignId);

	if (isNotFound(campaign)) return null;

	return <CampaignContent name={campaign.data.texts.name} texts={campaign.data.texts} />;
}

function CampaignContent(props: {
	name?: string;
	texts: { objectives?: string; resume?: string; activities?: string; benefits?: string; updateAndLinks?: string };
}) {
	return (
		<DivWithPrimaryColorVariable className="flex w-full flex-col gap-6">
			{isNotEmpty(props.name) && (
				<div
					className="text-primary text-3xl font-semibold"
					dangerouslySetInnerHTML={{
						__html: props.name,
					}}
				/>
			)}

			<CampaignTextCards texts={props.texts} />
		</DivWithPrimaryColorVariable>
	);
}

function isNotEmpty(text: string | undefined): text is string {
	return text !== undefined && text !== '' && text !== '-' && text !== '<p>-</p>';
}
